import type { CSSProperties, ReactNode } from "react";
import {
  ArrowRight,
  BadgeHelp,
  Check,
  LayoutDashboard,
  LifeBuoy,
  ListChecks,
  Mail,
  MapPin,
  PackageCheck,
  ReceiptText,
  Settings,
  ShieldCheck,
  TicketCheck,
  Users,
  WalletCards,
} from "lucide-react";
import {
  AccountShell,
  organizationLabels,
  roleLabels,
  type OrganizationDetail,
  type OrganizationTab,
} from "../../account";
import {
  organizationTabs,
  stepStatusLabel,
  workspaceNavigationLabels,
} from "../model";

const workspaceNavigationIcons = {
  overview: LayoutDashboard,
  areas: MapPin,
  permits: TicketCheck,
  team: Users,
  finance: WalletCards,
  package: PackageCheck,
  settings: Settings,
} satisfies Record<OrganizationTab, typeof LayoutDashboard>;

type OrganizationWorkspaceShellProps = {
  children: ReactNode;
  data: OrganizationDetail;
  message: string;
  tab: OrganizationTab;
  onTabChange: (tab: OrganizationTab) => void;
};

export function OrganizationWorkspaceShell({
  children,
  data,
  message,
  tab,
  onTabChange,
}: OrganizationWorkspaceShellProps) {
  const workspaceUserLabel = String(
    data.members.find((member) => member.status === "active")?.display_name ??
      "Organisaatiotili",
  );
  return (
    <AccountShell
      signedIn
      accountType="organization"
      workspace
      workspaceUserLabel={workspaceUserLabel}
    >
      <div className="org-workspace-shell">
        <aside className="org-workspace-sidebar">
          <header>
            <span>Organisaatio</span>
            <h1>{data.organization.name}</h1>
            <em className={data.organization.status === "active" ? "verified" : "pending"}>
              <ShieldCheck />
              {data.organization.status === "active" ? "Vahvistettu" : "Varmennus kesken"}
            </em>
          </header>
          <nav aria-label="Organisaation työtila">
            {workspaceNavigationLabels.map((item) => {
              const Icon = workspaceNavigationIcons[item.id];
              return (
                <button
                  key={item.id}
                  aria-current={tab === item.id ? "page" : undefined}
                  onClick={() => onTabChange(item.id)}
                >
                  <Icon />
                  {item.label}
                </button>
              );
            })}
          </nav>
          <section>
            <LifeBuoy />
            <b>Tarvitsetko apua?</b>
            <p>Katso ohjeet tai ota yhteyttä asiakaspalveluun.</p>
            <a href="/#help"><BadgeHelp /> Ohjeet</a>
            <a href="mailto:tuki@lupava.fi"><Mail /> Ota yhteyttä</a>
          </section>
        </aside>

        <main className="account-main organization-workspace">
          <header className="workspace-hero">
            <div>
              <span className="eyebrow">
                {organizationLabels[
                  data.organization.organization_type ??
                    data.organization.organizationType ??
                    ""
                ] ?? "Organisaatio"}
              </span>
              <h1>{data.organization.name}</h1>
              <p>
                {roleLabels[data.organization.role] ?? data.organization.role} ·{" "}
                {data.organization.status === "active"
                  ? "organisaatio varmennettu"
                  : "varmennus kesken"}
              </p>
            </div>
            <div className="workspace-progress">
              <strong>{data.onboarding.percent}%</strong>
              <span>{data.onboarding.completed}/{data.onboarding.total} vaihetta valmis</span>
              <div><i style={{ width: `${data.onboarding.percent}%` }} /></div>
            </div>
          </header>
          <nav className="workspace-tabs" aria-label="Organisaation työtila">
            {organizationTabs.map((item) => (
              <button
                key={item.id}
                aria-current={tab === item.id ? "page" : undefined}
                onClick={() => onTabChange(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          {message && <div className="notice info workspace-notice">{message}</div>}
          {children}
        </main>

        <aside className="org-workspace-rail">
          <section className="workspace-status-card">
            <span>Käyttöönoton tila</span>
            <div
              className="progress-ring"
              style={{ "--progress": `${data.onboarding.percent * 3.6}deg` } as CSSProperties}
            >
              <strong>{data.onboarding.percent}%</strong>
            </div>
            <b>
              Vaihe {Math.min(data.onboarding.completed + 1, data.onboarding.total)}/
              {data.onboarding.total}
            </b>
            <small>Organisaation tiedot</small>
          </section>
          <section className="workspace-process-card">
            <span>Prosessin vaiheet</span>
            {data.onboarding.steps.map((item, index) => (
              <button key={item.id} className={item.status} onClick={() => onTabChange(item.tab)}>
                <i>{item.status === "complete" ? <Check /> : index + 1}</i>
                <b>{item.label}<small>{stepStatusLabel(item.status)}</small></b>
              </button>
            ))}
          </section>
          <section className="workspace-benefits-card">
            <span>Hyödyt organisaatiolle</span>
            {[
              "Helppo alueiden hallinta",
              "Lupien myöntäminen ja myynti",
              "Automaattinen laskutus",
              "Selkeät raportit ja seuranta",
            ].map((item) => <p key={item}><Check />{item}</p>)}
          </section>
          <section className="workspace-help-card">
            <span>Ohjeet</span>
            <a href="/#help"><ListChecks /> Miten hakemus etenee?<ArrowRight /></a>
            <a href="/#help"><ReceiptText /> Tarvittavat liitteet<ArrowRight /></a>
            <a href="/#help"><BadgeHelp /> Usein kysytyt kysymykset<ArrowRight /></a>
          </section>
        </aside>
      </div>
    </AccountShell>
  );
}
